import { type ReactNode, useMemo } from 'react';

import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';

import { ItemType } from '@/components/item/itemType';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DisplayName } from '@/components/ui/display-name';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type {
  PublishSandboxItemStatus,
  PublishSandboxResponse,
} from '../models/publishSandbox';

const renderStatusBadge = (itemStatus: PublishSandboxItemStatus) => {
  switch (itemStatus) {
    case 'published':
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200"
        >
          <CheckCircle className="h-3 w-3 mr-1" />
          Published
        </Badge>
      );
    case 'failed':
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-200"
        >
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    case 'skipped':
      return (
        <Badge
          variant="outline"
          className="bg-gray-50 text-gray-700 border-gray-200"
        >
          <AlertCircle className="h-3 w-3 mr-1" />
          Skipped
        </Badge>
      );
  }
};

interface BasePublishResult {
  status: PublishSandboxItemStatus;
  error: string | null;
}

interface PublishResultsTableProps<T extends BasePublishResult> {
  title: string;
  data: T[];
  keyExtractor: (item: T) => string;
  renderName: (item: T) => ReactNode;
}

export const PublishResultsTable = <T extends BasePublishResult>({
  title,
  data,
  keyExtractor,
  renderName,
}: PublishResultsTableProps<T>) => {
  if (data.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow key={keyExtractor(item)}>
                <TableCell>{renderStatusBadge(item.status)}</TableCell>
                <TableCell className="font-medium">
                  {renderName(item)}
                </TableCell>
                <TableCell>
                  <div className="max-w-[45ch] overflow-hidden">
                    {item.error ? (
                      <span className="inline-block text-xs text-red-600 font-mono bg-red-50 px-2 py-1 rounded whitespace-break-spaces">
                        {item.error}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
interface SandboxPublishSummaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  response: PublishSandboxResponse;
}

export const SandboxPublishSummaryDialog = ({
  open,
  onOpenChange,
  response,
}: SandboxPublishSummaryDialogProps) => {
  const totalPublished =
    response.items.published +
    response.snapshots.published +
    response.gifts.published +
    response.season_passes.published;
  const totalFailed =
    response.items.failed +
    response.snapshots.failed +
    response.gifts.failed +
    response.season_passes.failed;
  const totalItems =
    response.items.total +
    response.snapshots.total +
    response.gifts.total +
    response.season_passes.total;

  const status = useMemo(() => {
    if (response.success && totalFailed === 0) return 'complete';
    if (totalPublished === 0) return 'failed';
    return 'partial';
  }, [response.success, totalFailed, totalPublished]);

  const statusConfig = useMemo(() => {
    switch (status) {
      case 'complete':
        return {
          icon: CheckCircle,
          iconColor: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-900',
          title: 'Publish completed successfully',
          message: `Everything went well.`,
        };
      case 'partial':
        return {
          icon: AlertCircle,
          iconColor: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-900',
          title: 'Publish partially completed',
          message: `Some resources were not published. Check the logs for details.`,
        };
      case 'failed':
      default:
        return {
          icon: XCircle,
          iconColor: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-900',
          title: 'Publish failed',
          message: `Check the logs for details.`,
        };
    }
  }, [status, totalItems, totalPublished, totalFailed]);

  const StatusIcon = statusConfig.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish Summary</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          {/* Status Header */}
          <Card
            className={`${statusConfig.borderColor} ${statusConfig.bgColor}`}
          >
            <CardContent>
              <div className="flex flex-row items-start gap-4">
                <div className={`${statusConfig.iconColor} mt-1`}>
                  <StatusIcon className="size-6" />
                </div>
                <div>
                  <h3
                    className={`text-lg font-semibold ${statusConfig.textColor}`}
                  >
                    {statusConfig.title}
                  </h3>
                  <p
                    className={`text-sm mt-1 ${statusConfig.textColor} opacity-80`}
                  >
                    {statusConfig.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          {/* Items Stats */}
          <div className="flex flex-wrap gap-4 justify-between">
            {response.items.total > 0 && (
              <Card className="flex-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-muted-foreground">
                        {response.items.total}
                      </div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.items.published > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                      >
                        {response.items.published}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Published
                      </div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.items.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                      >
                        {response.items.failed}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Failed
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Snapshots Stats */}
            {response.snapshots.total > 0 && (
              <Card className="flex-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Snapshots
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-muted-foreground">
                        {response.snapshots.total}
                      </div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.snapshots.published > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                      >
                        {response.snapshots.published}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Published
                      </div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.snapshots.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                      >
                        {response.snapshots.failed}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Failed
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gifts Stats */}
            {response.gifts.total > 0 && (
              <Card className="flex-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Gifts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-muted-foreground">
                        {response.gifts.total}
                      </div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.gifts.published > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                      >
                        {response.gifts.published}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Published
                      </div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.gifts.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                      >
                        {response.gifts.failed}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Failed
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Season Passes Stats */}
            {response.season_passes.total > 0 && (
              <Card className="flex-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Season Passes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-muted-foreground">
                        {response.season_passes.total}
                      </div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.season_passes.published > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                      >
                        {response.season_passes.published}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Published
                      </div>
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${response.season_passes.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                      >
                        {response.season_passes.failed}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Failed
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Items Table */}
          <PublishResultsTable
            title="Items Details"
            data={response.items.results}
            keyExtractor={(item) => item.item_id}
            renderName={(item) => (
              <div className="flex items-center gap-2">
                <ItemType type={item.type} size={16} />
                <DisplayName name={item.name} maxWidth="200px" />
              </div>
            )}
          />

          {/* Snapshots Table */}
          <PublishResultsTable
            title="Snapshots Details"
            data={response.snapshots.results}
            keyExtractor={(snapshot) => snapshot.snapshot_id}
            renderName={(snapshot) => (
              <DisplayName name={snapshot.name} maxWidth="250px" />
            )}
          />

          {/* Gifts Table */}
          <PublishResultsTable
            title="Gifts Details"
            data={response.gifts.results}
            keyExtractor={(gift) => gift.gift_id}
            renderName={(gift) => (
              <div className="flex flex-col">
                <span>{gift.code}</span>
                <DisplayName
                  name={gift.name}
                  maxWidth="200px"
                  className="text-xs text-muted-foreground"
                />
              </div>
            )}
          />

          {/* Season Passes Table (failures only) */}
          <PublishResultsTable
            title="Season Passes Details (failures)"
            data={response.season_passes.results}
            keyExtractor={(seasonPass) => seasonPass.season_pass_id}
            renderName={(seasonPass) => (
              <div className="flex flex-col">
                <span>{seasonPass.code}</span>
                <DisplayName
                  name={seasonPass.name}
                  maxWidth="200px"
                  className="text-xs text-muted-foreground"
                />
              </div>
            )}
          />
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
